import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { QpxButton, QpxIcon, QpxTooltip } from '../../../../directives';
import {
    FILE_PREVIEW_PANEL_SIDE,
    type FilePreviewAction,
    type FilePreviewPanelConfig,
    type PreviewZoom,
} from '../../models';
import {
    DOCUMENT_INFO_PANEL_ID,
    PreviewActionButtons,
    PreviewControlGroup,
    PreviewPanelButtons,
    PreviewPaneButtons,
    toPreviewPaneButtons,
    type PreviewPaneButton,
    PreviewToolbar,
    PreviewZoomControl,
    resolvePreviewPanelDomId,
} from '../../preview-controls';

@Component({
    selector: 'qpx-controls',
    imports: [
        MatButtonModule,
        QpxTooltip,
        NgIcon,
        QpxButton,
        QpxIcon,
        PreviewActionButtons,
        PreviewControlGroup,
        PreviewPanelButtons,
        PreviewPaneButtons,
        PreviewToolbar,
        PreviewZoomControl,
    ],
    templateUrl: './controls.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
})
export class Controls {
    protected readonly mobile = injectViewportBreakpoints().isPhone;

    //#region Inputs
    public readonly zoom = input.required<PreviewZoom>();
    public readonly controlsVisible = input(true);
    public readonly documentInfoVisible = input(false);
    public readonly handTool = input(false);
    public readonly actions = input<readonly FilePreviewAction[]>([]);
    public readonly leftPanels = input<readonly FilePreviewPanelConfig[]>([]);
    public readonly rightPanels = input<readonly FilePreviewPanelConfig[]>([]);
    public readonly activeLeftPanelId = input<string | null>(null);
    public readonly activeRightPanelId = input<string | null>(null);
    //#endregion

    //#region Outputs
    public readonly zoomOut = output<void>();
    public readonly zoomIn = output<void>();
    public readonly zoomReset = output<void>();
    public readonly zoomSelect = output<PreviewZoom>();
    public readonly rotateClockwise = output<void>();
    public readonly handToolToggle = output<void>();
    public readonly controlsToggle = output<void>();
    public readonly infoToggle = output<void>();
    public readonly panelToggle = output<FilePreviewPanelConfig>();
    public readonly download = output<void>();
    public readonly closePreview = output<void>();
    //#endregion

    //#region State
    protected readonly documentInfoPanelId = resolvePreviewPanelDomId(
        FILE_PREVIEW_PANEL_SIDE.Right,
        DOCUMENT_INFO_PANEL_ID
    );
    protected readonly handToolLabel = computed(() =>
        this.handTool() ? 'Turn off image hand tool' : 'Turn on image hand tool'
    );
    protected readonly controlsLabel = computed(() =>
        this.controlsVisible() ? 'Hide image controls' : 'Show image controls'
    );
    protected readonly documentInfoLabel = computed(() =>
        this.documentInfoVisible()
            ? 'Hide document information'
            : 'Show document information'
    );
    protected readonly rightPaneChoices = computed<
        readonly PreviewPaneButton[]
    >(() => [
        ...toPreviewPaneButtons(
            this.rightPanels(),
            this.activeRightPanelId(),
            (panel) => this.panelToggle.emit(panel),
            false
        ),
        {
            id: 'information',
            label: 'Document information',
            icon: 'lucideInfo',
            ariaLabel: this.documentInfoLabel(),
            active: this.documentInfoVisible(),
            disabled: false,
            controls: this.documentInfoPanelId,
            onSelect: () => this.infoToggle.emit(),
        },
    ]);
    protected readonly overflowActions = computed<readonly FilePreviewAction[]>(
        () => [
            {
                id: 'viewer-rotate',
                label: 'Rotate image clockwise',
                icon: 'lucideRotateCw',
                disabled: false,
                onSelect: () => this.rotateClockwise.emit(),
            },

            {
                id: 'viewer-download',
                label: 'Download image',
                icon: 'lucideDownload',
                disabled: false,
                onSelect: () => this.download.emit(),
            },
            ...this.actions(),
        ]
    );
    //#endregion
}
